import { WORKFLOW_NOTIFY_USER_CUSTOM_VIEW_KEY } from "../constants/workflowConstants";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type {
    FormFieldDefinition,
    FormFieldOption,
    FormFieldType,
    FormFieldVisibleWhen,
} from "../types/workflowFormField";
import type { NormalizedStepConfig } from "../types/workflowSharedTypes";
import {
    NOTIFY_USER_ACTION_CODE,
    NOTIFY_USER_MESSAGE_FALLBACK,
    readNotifyUserMessageFromEnumMeta,
} from "./notifyUserStaticMessage";

export type { NormalizedStepConfig } from "../types/workflowSharedTypes";

/**
 * Turn raw enum `meta` from the API into the shape the action step editor reads.
 * Invalid `formFields` entries are dropped. Notify User gets default view key + message when missing.
 */
export function normalizeStepConfig(
    meta: Record<string, unknown> | undefined | null,
    actionCode?: string
): NormalizedStepConfig {
    if (!meta || typeof meta !== "object") {
        return actionCode === NOTIFY_USER_ACTION_CODE ? applyNotifyUserDefaults({}, meta) : {};
    }

    const base = readMetaIntoStepConfig(meta);
    if (actionCode === NOTIFY_USER_ACTION_CODE) {
        return applyNotifyUserDefaults(base, meta);
    }
    return base;
}

function readMetaIntoStepConfig(meta: Record<string, unknown>): NormalizedStepConfig {
    const out: NormalizedStepConfig = {};

    if (typeof meta.configVersion === "number" && Number.isFinite(meta.configVersion)) {
        out.configVersion = meta.configVersion;
    }
    if (typeof meta.customViewKey === "string" && meta.customViewKey.trim()) {
        out.customViewKey = meta.customViewKey.trim();
    }
    const params = meta.customViewParams;
    if (params && typeof params === "object" && !Array.isArray(params)) {
        out.customViewParams = params as Record<string, unknown>;
    }

    const fields = parseFormFieldsArray(meta.formFields);
    if (fields) out.formFields = fields;

    return out;
}

function applyNotifyUserDefaults(
    base: NormalizedStepConfig,
    meta: Record<string, unknown> | null | undefined
): NormalizedStepConfig {
    const mergedParams: Record<string, unknown> = { ...(base.customViewParams ?? {}) };
    const existing = mergedParams.staticMessage;
    if (typeof existing !== "string" || !existing.trim()) {
        const fromMeta = readNotifyUserMessageFromEnumMeta(meta as WorkflowEnumItem["meta"] | undefined);
        mergedParams.staticMessage = fromMeta ?? NOTIFY_USER_MESSAGE_FALLBACK;
    }
    const out: NormalizedStepConfig = { ...base, customViewParams: mergedParams };
    if (!out.customViewKey?.trim()) {
        out.customViewKey = WORKFLOW_NOTIFY_USER_CUSTOM_VIEW_KEY;
    }
    return out;
}

// --- formFields: API JSON → typed defs (invalid items skipped)

const FIELD_TYPES: ReadonlySet<string> = new Set([
    "radio",
    "checkbox",
    "select",
    "text",
    "textarea",
    "number",
]);

const VISIBLE_WHEN_OPERATORS = new Set<NonNullable<FormFieldVisibleWhen["operator"]>>([
    "eq",
    "neq",
    "in",
    "notIn",
    "gt",
    "lt",
]);

function parseVisibleWhen(raw: unknown): FormFieldVisibleWhen | undefined {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const o = raw as Record<string, unknown>;
    const field = o.field;
    if (typeof field !== "string" || !field.trim()) return undefined;

    const opRaw = o.operator;
    const operator =
        typeof opRaw === "string" && VISIBLE_WHEN_OPERATORS.has(opRaw as NonNullable<FormFieldVisibleWhen["operator"]>)
            ? (opRaw as NonNullable<FormFieldVisibleWhen["operator"]>)
            : undefined;

    const val = o.value;
    let value: FormFieldVisibleWhen["value"];
    if (Array.isArray(val)) {
        const strings = val.filter((item): item is string => typeof item === "string");
        if (strings.length === 0) return undefined;
        value = strings;
    } else if (typeof val === "string" || typeof val === "number") {
        value = val;
    } else {
        return undefined;
    }

    return {
        field: field.trim(),
        ...(operator ? { operator } : {}),
        value,
    };
}

function parseOption(raw: unknown): FormFieldOption | null {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.value !== "string" || typeof o.label !== "string") return null;
    return {
        value: o.value,
        label: o.label,
        ...(typeof o.description === "string" ? { description: o.description } : {}),
    };
}

function parseField(raw: unknown): FormFieldDefinition | null {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const o = raw as Record<string, unknown>;
    const key = o.key;
    const type = o.type;
    const label = o.label;
    if (typeof key !== "string" || !key.trim()) return null;
    if (typeof type !== "string" || !FIELD_TYPES.has(type)) return null;
    if (typeof label !== "string" || !label.trim()) return null;

    const fieldType = type as FormFieldType;
    let options: FormFieldOption[] | undefined;
    if (fieldType === "radio" || fieldType === "select") {
        if (!Array.isArray(o.options)) return null;
        const parsed = o.options.map(parseOption).filter((x): x is FormFieldOption => x !== null);
        if (parsed.length === 0) return null;
        options = parsed;
    }

    const def: FormFieldDefinition = {
        key: key.trim(),
        type: fieldType,
        label: label.trim(),
        ...(options ? { options } : {}),
    };
    const dv = o.defaultValue;
    if (typeof dv === "string" || typeof dv === "number" || typeof dv === "boolean") def.defaultValue = dv;
    if (typeof o.required === "boolean") def.required = o.required;
    if (typeof o.min === "number") def.min = o.min;
    if (typeof o.max === "number") def.max = o.max;
    if (typeof o.placeholder === "string") def.placeholder = o.placeholder;
    if (typeof o.readOnly === "boolean") def.readOnly = o.readOnly;
    const visibleWhen = parseVisibleWhen(o.visibleWhen);
    if (visibleWhen) def.visibleWhen = visibleWhen;
    return def;
}

function parseFormFieldsArray(raw: unknown): FormFieldDefinition[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const fields = raw.map(parseField).filter((x): x is FormFieldDefinition => x !== null);
    return fields.length > 0 ? fields : undefined;
}
